/**
 * Test Booking Widget Page
 * 
 * Development utility to test the patient booking widget.
 * Allows creating test links or directly viewing the widget with mock data.
 */

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Copy, ExternalLink, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import BookingWidget from '@/components/patient-booking/BookingWidget';
import apiClient from '@/services/api';

const TestBookingWidget: React.FC = () => {
  const [mode, setMode] = useState<'direct' | 'link'>('direct');
  
  // Direct widget mode
  const [sessionToken, setSessionToken] = useState('test-session-token-' + Date.now());
  const [clinicId, setClinicId] = useState('');
  const [clinicName, setClinicName] = useState('Test Clinic');
  const [patientId, setPatientId] = useState('test-patient-' + Date.now());
  const [patientName, setPatientName] = useState('Test Patient');
  const [showWidget, setShowWidget] = useState(false);
  
  // Link creation mode
  const [linkClinicId, setLinkClinicId] = useState('');
  const [linkPatientId, setLinkPatientId] = useState('');
  const [linkPurpose, setLinkPurpose] = useState<'appointment_booking' | 'appointment_reschedule'>('appointment_booking');
  const [createdLink, setCreatedLink] = useState<string | null>(null);
  const [isCreatingLink, setIsCreatingLink] = useState(false);
  
  const { toast } = useToast();

  const handleDirectView = () => {
    if (!clinicId) {
      toast({
        title: 'Missing Clinic ID',
        description: 'Please enter a clinic ID to test the widget.',
        variant: 'destructive',
      });
      return;
    }
    setShowWidget(true);
  };

  const handleCreateLink = async () => {
    if (!linkClinicId || !linkPatientId) {
      toast({
        title: 'Missing Information',
        description: 'Please enter both Clinic ID and Patient ID.',
        variant: 'destructive',
      });
      return;
    }

    setIsCreatingLink(true);
    try {
      // Try to create a patient action link via API
      // Note: This endpoint may not exist - adjust based on your backend API
      const response = await apiClient.post('/clinic/patient-links/create', {
        clinic_id: linkClinicId,
        patient_id: linkPatientId,
        purpose: linkPurpose,
      });

      if (response.data.success) {
        const token = response.data.data.token;
        const link = `${window.location.origin}/p/${token}`;
        setCreatedLink(link);
        toast({
          title: 'Link Created!',
          description: 'Patient booking link has been generated.',
        });
      }
    } catch (error: any) {
      // If API doesn't exist, create a mock link for testing
      console.warn('API endpoint not available, creating mock link:', error);
      const mockToken = `test-${linkPurpose}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const link = `${window.location.origin}/p/${mockToken}`;
      setCreatedLink(link);
      toast({
        title: 'Mock Link Created',
        description: 'Created a test link. Note: This will require a valid backend token to work.',
        variant: 'default',
      });
    } finally {
      setIsCreatingLink(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Copied!',
      description: 'Link copied to clipboard.',
    });
  };

  const openLink = (link: string) => {
    window.open(link, '_blank');
  };

  if (showWidget && mode === 'direct') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
        <div className="w-full max-w-lg">
          <div className="text-center mb-6">
            <img src="/novamed_logo_white.svg" alt="NovaMed" className="mx-auto h-8 mb-2" />
            <p className="text-slate-400 text-sm">{clinicName}</p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowWidget(false)}
              className="mt-4 text-slate-400 hover:text-white"
            >
              ← Back to Test Settings
            </Button>
          </div>
          <BookingWidget
            sessionToken={sessionToken}
            clinicId={clinicId}
            clinicName={clinicName}
            patientId={patientId}
            patientName={patientName}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 max-w-4xl">
      <Card>
        <CardHeader>
          <CardTitle>Test Patient Booking Widget</CardTitle>
          <CardDescription>
            Development utility to test the patient booking widget. Use direct mode for quick testing
            or create a test link to simulate the full patient flow.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={mode} onValueChange={(v) => setMode(v as 'direct' | 'link')}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="direct">Direct Widget View</TabsTrigger>
              <TabsTrigger value="link">Create Test Link</TabsTrigger>
            </TabsList>

            <TabsContent value="direct" className="space-y-4 mt-6">
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Direct mode shows the widget immediately with test data. API calls will still be made,
                  so ensure your backend is running and configured.
                </AlertDescription>
              </Alert>

              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="clinicId">Clinic ID *</Label>
                  <Input
                    id="clinicId"
                    value={clinicId}
                    onChange={(e) => setClinicId(e.target.value)}
                    placeholder="Enter clinic ID"
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="clinicName">Clinic Name</Label>
                  <Input
                    id="clinicName"
                    value={clinicName}
                    onChange={(e) => setClinicName(e.target.value)}
                    placeholder="Test Clinic"
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="patientId">Patient ID</Label>
                  <Input
                    id="patientId"
                    value={patientId}
                    onChange={(e) => setPatientId(e.target.value)}
                    placeholder="Auto-generated"
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="patientName">Patient Name</Label>
                  <Input
                    id="patientName"
                    value={patientName}
                    onChange={(e) => setPatientName(e.target.value)}
                    placeholder="Test Patient"
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="sessionToken">Session Token</Label>
                  <Input
                    id="sessionToken"
                    value={sessionToken}
                    onChange={(e) => setSessionToken(e.target.value)}
                    placeholder="Auto-generated"
                  />
                </div>

                <Button onClick={handleDirectView} className="w-full">
                  View Booking Widget
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="link" className="space-y-4 mt-6">
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Create a test patient action link. This simulates the real patient flow with OTP verification.
                  The link will point to <code className="text-xs">/p/:token</code>.
                </AlertDescription>
              </Alert>

              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="linkClinicId">Clinic ID *</Label>
                  <Input
                    id="linkClinicId"
                    value={linkClinicId}
                    onChange={(e) => setLinkClinicId(e.target.value)}
                    placeholder="Enter clinic ID"
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="linkPatientId">Patient ID *</Label>
                  <Input
                    id="linkPatientId"
                    value={linkPatientId}
                    onChange={(e) => setLinkPatientId(e.target.value)}
                    placeholder="Enter patient ID"
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="linkPurpose">Link Purpose</Label>
                  <select
                    id="linkPurpose"
                    value={linkPurpose}
                    onChange={(e) => setLinkPurpose(e.target.value as 'appointment_booking' | 'appointment_reschedule')}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                  >
                    <option value="appointment_booking">Appointment Booking</option>
                    <option value="appointment_reschedule">Appointment Reschedule</option>
                  </select>
                </div>

                <Button 
                  onClick={handleCreateLink} 
                  disabled={isCreatingLink}
                  className="w-full"
                >
                  {isCreatingLink ? 'Creating Link...' : 'Create Test Link'}
                </Button>

                {createdLink && (
                  <div className="space-y-2 p-4 border rounded-lg bg-muted/50">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      <Label className="text-sm font-medium">Test Link Created</Label>
                    </div>
                    <div className="flex gap-2">
                      <Input
                        value={createdLink}
                        readOnly
                        className="font-mono text-xs"
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => copyToClipboard(createdLink)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => openLink(createdLink)}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Click the link to test the full patient booking flow with OTP verification.
                    </p>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default TestBookingWidget;



